/** SPECIFICATION-adjacent: design document E1 — sheet setup. */

/** 1 PostScript point = 1/72 inch. */
export const PT_PER_MM = 72 / 25.4;

export const mmToPt = (mm: number): number => mm * PT_PER_MM;
export const ptToMm = (pt: number): number => pt / PT_PER_MM;

export type SheetSizeId =
  | 'A4' | 'A3' | 'A2' | 'A1' | 'A0'
  | 'Letter' | 'Tabloid'
  | 'ARCH-B' | 'ARCH-C' | 'ARCH-D';

export interface SheetSize {
  readonly id: SheetSizeId;
  readonly label: string;
  /** Short edge, millimetres. */
  readonly shortMm: number;
  /** Long edge, millimetres. */
  readonly longMm: number;
}

export const SHEET_SIZES: readonly SheetSize[] = [
  { id: 'A4', label: 'A4', shortMm: 210, longMm: 297 },
  { id: 'A3', label: 'A3', shortMm: 297, longMm: 420 },
  { id: 'A2', label: 'A2', shortMm: 420, longMm: 594 },
  { id: 'A1', label: 'A1', shortMm: 594, longMm: 841 },
  { id: 'A0', label: 'A0', shortMm: 841, longMm: 1189 },
  { id: 'Letter', label: 'US Letter', shortMm: 215.9, longMm: 279.4 },
  { id: 'Tabloid', label: 'US Tabloid', shortMm: 279.4, longMm: 431.8 },
  { id: 'ARCH-B', label: 'ARCH B', shortMm: 304.8, longMm: 457.2 },
  { id: 'ARCH-C', label: 'ARCH C', shortMm: 457.2, longMm: 609.6 },
  { id: 'ARCH-D', label: 'ARCH D', shortMm: 609.6, longMm: 914.4 },
];

export function sheetSize(id: SheetSizeId): SheetSize {
  const found = SHEET_SIZES.find((size) => size.id === id);
  if (found === undefined) throw new Error(`Unknown sheet size: ${id}`);
  return found;
}

export type Orientation = 'portrait' | 'landscape';

export interface SheetDimensions {
  readonly widthMm: number;
  readonly heightMm: number;
}

export function sheetDimensions(id: SheetSizeId, orientation: Orientation): SheetDimensions {
  const size = sheetSize(id);
  return orientation === 'landscape'
    ? { widthMm: size.longMm, heightMm: size.shortMm }
    : { widthMm: size.shortMm, heightMm: size.longMm };
}

/**
 * Offered ratios. The scale is CHOSEN, never fitted — a drawing at "whatever
 * fits" is not a drawing, it is a picture (design document E1).
 */
export const SCALE_RATIOS = [5, 10, 20, 25, 50, 100] as const;
export type ScaleRatio = (typeof SCALE_RATIOS)[number];

/** Millimetres of paper for a length of wall, at 1:ratio. */
export const worldToPaperMm = (worldMm: number, ratio: number): number => worldMm / ratio;

/** Millimetres of wall for a length of paper, at 1:ratio. */
export const paperToWorldMm = (paperMm: number, ratio: number): number => paperMm * ratio;

/** Sheet furniture reserves, in millimetres of paper. */
export const SHEET_MARGIN_MM = 12;
export const TITLE_BLOCK_HEIGHT_MM = 26;
export const GAUGE_ROD_WIDTH_MM = 16;
export const DIMENSION_BAND_MM = 12;
/** Printed overlap between adjacent tiles (design document E2). */
export const TILE_OVERLAP_MM = 10;

export interface DrawableArea {
  /** Paper-space origin of the drawable box, measured from the sheet's lower left. */
  readonly xMm: number;
  readonly yMm: number;
  readonly widthMm: number;
  readonly heightMm: number;
}

export interface SheetLayoutOptions {
  readonly sheet: SheetSizeId;
  readonly orientation: Orientation;
  readonly showGaugeRod: boolean;
  readonly showDimensions: boolean;
}

/** The area left for wall geometry once the furniture has taken its space. */
export function drawableArea(options: SheetLayoutOptions): DrawableArea {
  const { widthMm, heightMm } = sheetDimensions(options.sheet, options.orientation);
  const left = SHEET_MARGIN_MM + (options.showGaugeRod ? GAUGE_ROD_WIDTH_MM : 0);
  const right = SHEET_MARGIN_MM;
  const bottom = SHEET_MARGIN_MM + TITLE_BLOCK_HEIGHT_MM + (options.showDimensions ? DIMENSION_BAND_MM : 0);
  const top = SHEET_MARGIN_MM;
  const width = widthMm - left - right;
  const height = heightMm - bottom - top;
  if (width <= 0 || height <= 0) {
    throw new Error('This sheet is too small for the chosen furniture');
  }
  return { xMm: left, yMm: bottom, widthMm: width, heightMm: height };
}
